import { createContext, useContext } from "react";
import type { Branding } from "@/api/branding";

/**
 * The platform-default brand. Used as the context value until (and unless) a
 * dealer brand resolves. `is_default: true` guarantees consumers fall back to
 * the bundled assets and the CSS-var color defaults from `src/index.css`, so
 * the app renders pixel-identical to the pre-white-label build.
 */
export const DEFAULT_BRANDING: Branding = {
  brand_name: "",
  logo_url: "",
  logo_dark_url: "",
  favicon_url: "",
  colors: { primary: "", sidebar: "", accent: "" },
  active_template: "classic",
  header_links: [],
  footer_links: [],
  support_url: "",
  is_default: true,
};

/**
 * The brand context object. Exported so call sites (and tests) can supply an
 * explicit brand via `<BrandContext.Provider value={...}>` without going through
 * the fetching `BrandProvider`.
 */
export const BrandContext = createContext<Branding>(DEFAULT_BRANDING);

/** Read the active brand. Defaults to the platform brand outside a provider. */
export function useBranding(): Branding {
  return useContext(BrandContext);
}

/**
 * Converts a `#RGB`/`#RRGGBB` hex string to the shadcn `"H S L"` channel string
 * (space-separated, `%`-suffixed S/L, no `hsl()` wrapper) that `hsl(var(--x))`
 * expects. Returns `null` for empty/invalid input so the caller leaves the CSS
 * default in place.
 *
 * One-decimal precision is kept because the brand defaults require it to
 * round-trip the exact hex (e.g. `#0073EA` == `210.5 100% 45.9%`); integer
 * rounding shifts the rendered color.
 */
export function hexToHslTriplet(hex: string): string | null {
  const value = hex.trim();
  const match = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(value);
  if (!match) {
    return null;
  }
  let body = match[1];
  if (body.length === 3) {
    body = body
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const r = parseInt(body.slice(0, 2), 16) / 255;
  const g = parseInt(body.slice(2, 4), 16) / 255;
  const b = parseInt(body.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  const round1 = (n: number) => Math.round(n * 10) / 10;
  const hDeg = round1(h * 360);
  const sPct = round1(s * 100);
  const lPct = round1(l * 100);
  return `${hDeg} ${sPct}% ${lPct}%`;
}

/**
 * Derives a darker "hover" variant of a shadcn `"H S% L%"` channel triplet by
 * lowering only the lightness by `deltaL` percentage points. Used to compute
 * `--brand-primary-hover` from a dealer's primary so button hover states track
 * the active brand instead of a fixed blue.
 *
 * The new lightness is floored at 6% (never lower) so an already-dark brand
 * still produces a visibly darker — never lighter, never black — hover shade.
 * Hue and saturation are preserved unchanged; one-decimal rounding matches
 * {@link hexToHslTriplet}. Returns `null` for `null`/empty/invalid input so the
 * caller leaves the CSS default in place.
 */
export function darkenHslTriplet(
  triplet: string | null,
  deltaL = 8,
): string | null {
  if (!triplet) {
    return null;
  }
  const match =
    /^\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%\s*$/.exec(
      triplet,
    );
  if (!match) {
    return null;
  }
  const h = Number(match[1]);
  const s = Number(match[2]);
  const l = Number(match[3]);
  const round1 = (n: number) => Math.round(n * 10) / 10;
  const lHover = Math.max(6, l - deltaL);
  return `${round1(h)} ${round1(s)}% ${round1(lHover)}%`;
}
